import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import type { Neighborhood } from '../../shared/cityFile';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { PlaceSuggestion } from '../schedule/placeSearch';
import AddressSearch from './AddressSearch';
import type { Home } from './home';
import { Field, PickerButton, TextButton, inputStyle } from './styles';

/**
 * Where home is: a neighborhood from the city file, or an address searched on
 * Photon (OpenStreetMap), then Set home. Inline in the filters' distance
 * section, and in a popover from the map's home marker.
 *
 * Nothing is kept until Set home: choosing a neighborhood and then typing an
 * address leaves whichever was chosen last waiting beside the button.
 */

const Select = styled('select')({
  ...inputStyle,
  appearance: 'none',
  paddingRight: 30,
  cursor: 'pointer',
  // A chevron, drawn in parchment so it reads on the dark field.
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23f3eee7' stroke-width='1.6'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  '& option, & optgroup': { color: BRAND.mocha, background: BRAND.parchment },
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
});

const Pending = styled('div')({
  flex: '1 1 140px',
  minWidth: 0,
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 600,
  color: 'rgba(243,238,231,0.85)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

type Choice = Omit<Home, 'mode'>;

interface Props {
  neighborhoods: Neighborhood[];
  home: Home | null;
  onSetHome: (home: Choice) => void;
  /** Present when the browser can be asked; the picker then offers "Use my current location". */
  onUseCurrent?: () => void;
  locating?: boolean;
  /** Offered while a home exists: close without changing it. */
  onCancel?: () => void;
  /** Focus the first field on open: true in the map's popover. */
  autoFocus?: boolean;
}

export default function HomePicker({ neighborhoods, home, onSetHome, onUseCurrent, locating, onCancel, autoFocus }: Props) {
  const [pending, setPending] = useState<Choice | null>(null);
  const [selectKey, setSelectKey] = useState('');
  const [query, setQuery] = useState('');
  const select = useRef<HTMLSelectElement | null>(null);
  const ids = useId();

  useEffect(() => {
    if (autoFocus) select.current?.focus();
  }, [autoFocus]);

  const boroughs = useMemo(() => {
    const groups = new Map<string, Neighborhood[]>();
    for (const n of neighborhoods) groups.set(n.borough, [...(groups.get(n.borough) ?? []), n]);
    for (const list of groups.values()) list.sort((a, b) => a.label.localeCompare(b.label));
    return [...groups];
  }, [neighborhoods]);

  const chooseNeighborhood = (key: string) => {
    setSelectKey(key);
    const n = neighborhoods.find((x) => x.key === key);
    if (!n) return;
    setPending({ label: `${n.label}, ${n.borough}`, lat: n.lat, lng: n.lng, source: 'neighborhood' });
    setQuery('');
  };

  const chooseAddress = (s: PlaceSuggestion) => {
    setQuery(s.label);
    setSelectKey('');
    setPending({ label: s.label, lat: s.coords[0], lng: s.coords[1], source: 'address' });
  };

  return (
    <div>
      <Field>
        <label htmlFor={`${ids}-n`}>Neighborhood</label>
        <Select id={`${ids}-n`} ref={select} value={selectKey} onChange={(e) => chooseNeighborhood(e.target.value)}>
          <option value="" disabled>
            Choose a neighborhood…
          </option>
          {boroughs.map(([borough, list]) => (
            <optgroup key={borough} label={borough}>
              {list.map((n) => (
                <option key={n.key} value={n.key}>
                  {n.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      </Field>

      <AddressSearch id={`${ids}-a`} label="Or an address" query={query} onQuery={setQuery} onChoose={chooseAddress} />

      <Row style={{ marginTop: 12 }}>
        <PickerButton type="button" disabled={!pending} onClick={() => pending && onSetHome(pending)}>
          Set home
        </PickerButton>
        {pending ? (
          <Pending title={pending.label}>{pending.label}</Pending>
        ) : (
          onCancel && home && <TextButton type="button" onClick={onCancel}>Keep {home.label}</TextButton>
        )}
      </Row>

      {onUseCurrent && (
        <Row style={{ marginTop: 12 }}>
          <TextButton type="button" onClick={onUseCurrent} disabled={locating}>
            {locating ? 'Finding you…' : 'Use my current location instead'}
          </TextButton>
        </Row>
      )}
    </div>
  );
}
